import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const Maintenance = () => {
  const [systemSettings, setSystemSettings] = useState({
    maintenanceMessage: 'Sistema temporariamente fora do ar. Estamos trabalhando para melhorar sua experiência.',
    estimatedReturn: null
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSystemSettings = async () => {
      try {
        const configDoc = await getDoc(doc(db, 'config', 'systemSettings'));
        if (configDoc.exists()) {
          const data = configDoc.data();
          if (data.estimatedReturn && data.estimatedReturn.toDate) {
            data.estimatedReturn = data.estimatedReturn.toDate();
          }
          setSystemSettings(data);
        }
      } catch (error) {
        console.error('Erro ao carregar configurações do sistema:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSystemSettings();
  }, []);

  const formatEstimatedReturn = (date) => {
    if (!date) return null;
    
    const now = new Date();
    const returnDate = new Date(date);
    const diffTime = returnDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) {
      return 'Em breve';
    } else if (diffDays === 1) {
      return 'Amanhã';
    } else {
      return `Em ${diffDays} dias`;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
      <div className="max-w-2xl w-full text-center">
        
        {/* Ícone de Manutenção */}
        <div className="mb-8">
          <div className="mx-auto w-24 h-24 bg-red-900/50 rounded-full flex items-center justify-center mb-6">
            <svg className="w-12 h-12 text-red-400 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          
          <h1 className="text-4xl font-bold text-white mb-4">
            Sistema Temporariamente Fora do Ar
          </h1>
          
          <div className="w-24 h-1 bg-gradient-to-r from-red-500 to-orange-500 mx-auto rounded-full"></div>
        </div>

        {/* Mensagem Principal */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 mb-8 border border-gray-700">
          <p className="text-xl text-gray-200 leading-relaxed">
            {systemSettings.maintenanceMessage}
          </p>
        </div>

        {/* Informações Adicionais */}
        <div className="space-y-6">
          
          {/* Data Estimada de Retorno */}
          {systemSettings.estimatedReturn && (
            <div className="bg-blue-900/30 border border-blue-700/50 rounded-xl p-6">
              <div className="flex items-center justify-center space-x-3 mb-2">
                <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-semibold text-blue-300">Retorno Estimado</h3>
              </div>
              <p className="text-blue-200 font-medium">
                {formatEstimatedReturn(systemSettings.estimatedReturn)}
              </p>
              <p className="text-blue-300 text-sm mt-1">
                {new Date(systemSettings.estimatedReturn).toLocaleDateString('pt-BR', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }).replace(/^\w/, c => c.toUpperCase())}
              </p>
            </div>
          )}

          {/* Informações de Contato */}
          <div className="bg-gray-800/30 border border-gray-600/50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-300 mb-3">Precisa de Ajuda?</h3>
            <p className="text-gray-400 text-sm">
              Se você tem uma conta master e precisa acessar o sistema administrativo, 
              entre em contato com o suporte técnico.
            </p>
          </div>

          {/* Contador de Tempo */}
          {systemSettings.estimatedReturn && (
            <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-700/50 rounded-xl p-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-3xl font-bold text-purple-300" id="hours">
                    --:--
                  </div>
                  <div className="text-sm text-purple-400">Horas</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-300" id="minutes">
                    --:--
                  </div>
                  <div className="text-sm text-blue-400">Minutos</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-indigo-300" id="seconds">
                    --:--
                  </div>
                  <div className="text-sm text-indigo-400">Segundos</div>
                </div>
              </div>
              <p className="text-center text-gray-400 text-sm mt-4">
                Tempo restante estimado
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-700">
          <p className="text-gray-500 text-sm">
            Obrigado pela sua paciência enquanto trabalhamos para melhorar nosso serviço.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Maintenance;
